/**
 * Thin AgentPermissionProvider for the shell
 *
 * This is a minimal context provider that stores entityId, authToken, and baseURL
 * for use by AgentPermissionGate. No Effect dependencies.
 */

import React, { createContext, useContext } from 'react';

export interface AgentPermissionContextValue {
  entityId: string;
  authToken: string;
  baseURL: string;
}

const AgentPermissionContext = createContext<AgentPermissionContextValue | null>(null);

export interface AgentPermissionProviderProps {
  /** Entity ID (UUID) for the current context */
  entityId: string;
  /** Bearer token for API authentication */
  authToken: string;
  /** Base URL for API requests and module loading */
  baseURL: string;
  /** React children */
  children: React.ReactNode;
}

/**
 * Minimal provider for permission checking.
 *
 * Use this with AgentPermissionGate when you need to gate UI elements based on permissions
 * outside of the OgAssist component (e.g., NavBar's SwitchMenuItem).
 *
 * @example
 * ```tsx
 * <AgentPermissionProvider entityId={entityId} authToken={token} baseURL={url}>
 *   <AgentPermissionGate>
 *     <MyProtectedComponent />
 *   </AgentPermissionGate>
 * </AgentPermissionProvider>
 * ```
 */
export const AgentPermissionProvider: React.FC<AgentPermissionProviderProps> = ({
  entityId,
  authToken,
  baseURL,
  children
}) => {
  const contextValue: AgentPermissionContextValue = {
    entityId,
    authToken,
    baseURL
  };

  return <AgentPermissionContext.Provider value={contextValue}>{children}</AgentPermissionContext.Provider>;
};

/**
 * Hook to access the AgentPermissionContext.
 * Must be used within AgentPermissionProvider.
 */
export const useAgentPermissionContext = (): AgentPermissionContextValue => {
  const context = useContext(AgentPermissionContext);
  if (!context) {
    throw new Error('useAgentPermissionContext must be used within AgentPermissionProvider');
  }
  return context;
};
